/**
 * 카드가 검색어에 걸린 이유 한 줄 — 검색어가 들어간 실제 문장 조각(앞뒤 몇 글자)을 뽑는다.
 *
 * 검색어 전체가 없으면 띄어쓰기로 나눈 낱말 중 가장 긴 것부터 찾는다. 본문에 없고 작성자명(브랜드·계정)에 있으면 「작성자명」으로 알린다.
 * 본문에도 이름에도 없으면 `null` — 확장 검색(유의어·다국어)이나 AI 판별로 걸린 경우다.
 */

export type MatchSnippet =
  | { kind: 'text'; before: string; hit: string; after: string }
  | { kind: 'name'; name: string }
  | { kind: 'lead'; text: string };

/** 조각 앞뒤로 남길 글자 수 */
const RADIUS = 28;

function clean(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function findIn(textValue: string, needle: string): number {
  return needle ? textValue.toLowerCase().indexOf(needle.toLowerCase()) : -1;
}

/**
 * @param query 확정 검색어 — 비면 `null`
 * @param texts 찾을 본문들(제목 · 본문 · 해시태그 등) — 앞에서부터 본다
 * @param names 이름들(브랜드 · 계정) — 본문에 없을 때만 본다
 */
export function findMatchSnippet(query: string, texts: string[], names: string[] = []): MatchSnippet | null {
  const q = clean(query);
  if (!q) return null;
  const needles = [q, ...q.split(' ').filter((w) => w.length >= 2).sort((a, b) => b.length - a.length)];

  for (const needle of needles) {
    for (const raw of texts) {
      const textValue = clean(raw);
      const at = findIn(textValue, needle);
      if (at >= 0) {
        const start = Math.max(0, at - RADIUS);
        const end = Math.min(textValue.length, at + needle.length + RADIUS);
        return {
          kind: 'text',
          before: `${start > 0 ? '…' : ''}${textValue.slice(start, at)}`,
          hit: textValue.slice(at, at + needle.length),
          after: `${textValue.slice(at + needle.length, end)}${end < textValue.length ? '…' : ''}`,
        };
      }
    }
  }
  for (const needle of needles) {
    const name = names.find((n) => findIn(n, needle) >= 0);
    if (name) return { kind: 'name', name };
  }
  return null;
}

/**
 * 카드에서 **찾아볼 본문들** — 갈래마다 글이 담긴 칸이 다르다.
 *
 * 🔑 소재 찾기와 허브가 **같은 목록**을 봐야 한다 — 한쪽만 해시태그를 안 보면 같은 카드가
 *   한 화면에서는 발췌가 뜨고 다른 화면에서는 안 뜬다.
 */
export function cardMatchTexts(card: {
  kind: 'ad' | 'organic' | 'product';
  ad?: { title?: unknown; bodyText?: unknown; caption?: unknown; linkDescription?: unknown };
  product?: { title?: unknown; eventInfo?: unknown };
  post?: Record<string, unknown>;
}): string[] {
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  if (card.kind === 'ad') return [str(card.ad?.title), str(card.ad?.bodyText), str(card.ad?.caption), str(card.ad?.linkDescription)];
  if (card.kind === 'product') return [str(card.product?.title), str(card.product?.eventInfo)];
  const tags = Array.isArray(card.post?.hashtags) ? (card.post?.hashtags as unknown[]).map(str).join(' ') : '';
  return [str(card.post?.title), str(card.post?.content), tags];
}

/**
 * 찾는 말이 본문에 **없을 때 대신 보여 줄 글머리**.
 *
 * 🔑 허브가 쓴다 — 거기서 찾는 말은 «정규 브랜드명»(`메디큐브`)인데 글은 별칭으로 적는다
 *   (`medicube`·`MEDICUBE`). 색인은 정규값으로 걸었으니 글은 맞는데 글자는 없는 것이 **정상**이라,
 *   「본문 밖에서 찾음」만 늘어놓으면 모든 카드가 같은 말을 한다.
 * ⛔ 검색어가 있는 자리(소재 찾기)에서는 쓰지 마라 — 거기서 「왜 걸렸나」는 진짜 질문이다.
 */
export function leadSnippet(texts: string[], limit = LEAD_LIMIT): MatchSnippet | null {
  const head = texts.map(clean).find(Boolean) ?? '';
  if (!head) return null;
  return { kind: 'lead', text: head.length > limit ? `${head.slice(0, limit)}…` : head };
}

/** 글머리로 남길 글자 수 — 카드 아래 두 줄에 들어가는 만큼 */
const LEAD_LIMIT = 80;
